'use client'

import { useState, useEffect } from 'react'
import { Clock, Star, Shield, ArrowLeft, ArrowRight } from 'lucide-react'
import './HeroSection.css'

const slideContent = [
  {
    title: "🍽️ Food Delivery",
    highlight: "Food Delivered",
    block: "Hot & Fresh",
    description: "Get your favorite meals delivered hot & fast to your doorstep!",
    stats: [
      { number: "21+", label: "Restaurants" },
      { number: "200+", label: "Happy Customers" },
      { number: "25min", label: "Avg Delivery" },
    ],
  },
  {
    title: "📦 Courier Delivery",
    highlight: "Fast & Safe",
    block: "Courier Services",
    description: "Swift and secure package delivery within the city. Track your courier in real-time.",
    stats: [
      { number: "50+", label: "Parcels Delivered" },
      { number: "99.9%", label: "Safe Delivery" },
      { number: "24/7", label: "Support" },
    ],
  },
  {
    title: "🎉 Party Zone",
    highlight: "Celebrate",
    block: "In Style",
    description: "Book party essentials, decorators, music, and food all from one platform.",
    stats: [
      { number: "100+", label: "Events Hosted" },
      { number: "150+", label: "Vendors" },
      { number: "Top Rated", label: "Services" },
    ],
  },
]

export default function HeroSection() {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlideIndex((prev) => (prev + 1) % slideContent.length)
    }, 5000)

    return () => clearInterval(interval)
  }, [])

  const currentSlide = slideContent[currentSlideIndex]

  return (
    <div className="min-h-screen">
      <div className="container-fluid hero-section">
        <div className="container">
          <div className="row align-items-center">

            {/* LEFT */}
            <div className="col-lg-6 col-md-12 mb-5">
              <div key={currentSlideIndex} className="hero-text-content">
                <h1 className="hero-title">
                  {currentSlide.title}
                  <span className="title-highlight">{currentSlide.highlight}</span>
                  <span className="title-block">{currentSlide.block}</span>
                </h1>

                <p className="hero-description">
                  {currentSlide.description}
                </p>
              </div>

              <div className="row text-center mt-4">
                {currentSlide.stats.map((stat, idx) => (
                  <div key={idx} className="col-4">
                    <div className="stat-number">{stat.number}</div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* STORE BUTTONS FIXED */}
              <div className="d-flex flex-wrap gap-3 mt-4 align-items-center">

                {/* GOOGLE PLAY */}
                <a
                  href="https://play.google.com/store/apps/details?id=com.katchit.customer_app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="store-link"
                  style={{
                    display: "inline-block",
                    zIndex: 99,
                    position: "relative",
                    textDecoration: "none"
                  }}
                >
                  <img
                    src="/assets/play_store.avif"
                    alt="Google Play"
                    className="app-store-img"
                    style={{
                      width: "180px",
                      height: "auto",
                      cursor: "pointer",
                      pointerEvents: "auto",
                      display: "block"
                    }}
                  />
                </a>

                {/* APP STORE */}
                <a
                  href="https://apps.apple.com/in/app/katchit-customer/id6785157187"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="store-link"
                  style={{
                    display: "inline-block",
                    zIndex: 99,
                    position: "relative",
                    textDecoration: "none"
                  }}
                >
                  <img
                    src="/assets/app_store.avif"
                    alt="App Store"
                    className="app-store-img"
                    style={{
                      width: "180px",
                      height: "auto",
                      cursor: "pointer",
                      pointerEvents: "auto",
                      display: "block"
                    }}
                  />
                 </a>
              </div>

              {/* SLIDER */}
              <div className="d-none align-items-center mt-4">
                {slideContent.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlideIndex(index)}
                    className={`slider-dot ${currentSlideIndex === index ? 'active' : ''}`}
                  />
                ))}

                <button
                  onClick={() =>
                    setCurrentSlideIndex(
                      (prev) => (prev - 1 + slideContent.length) % slideContent.length
                    )
                  }
                  className="btn btn-outline-light rounded-circle ms-3"
                >
                  <ArrowLeft />
                </button>

                <button
                  onClick={() =>
                    setCurrentSlideIndex(
                      (prev) => (prev + 1) % slideContent.length
                    )
                  }
                  className="btn btn-outline-light rounded-circle ms-2"
                >
                  <ArrowRight />
                </button>
              </div>
            </div>

            {/* RIGHT */}
            <div className="col-lg-6 col-md-12 text-center text-lg-end">
              <div className="position-relative d-inline-block">
                <img
                  src="/assets/home_new.jpeg"
                  alt="Hero"
                  className="hero-image img-fluid rounded shadow"
                />

                <div className="floating-card floating-card-1">
                  <div className="floating-card-icon floating-card-icon-green">
                    <Clock />
                  </div>
                  <div>
                    <div className="floating-card-title">Fast Delivery</div>
                    <div className="floating-card-subtitle">Under 30 mins</div>
                  </div>
                </div>

                <div className="floating-card floating-card-2">
                  <div className="floating-card-icon floating-card-icon-yellow">
                    <Star />
                  </div>
                  <div>
                    <div className="floating-card-title">4.5 Rating</div>
                    <div className="floating-card-subtitle">1.2K+ Reviews</div>
                  </div>
                </div>

                <div className="floating-card floating-card-3">
                  <div className="floating-card-icon floating-card-icon-red">
                    <Shield />
                  </div>
                  <div>
                    <div className="floating-card-title">Safe & Secure</div>
                    <div className="floating-card-subtitle">100% Protected</div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* WAVE */}
          <div className="wave-container">
            <svg viewBox="0 0 1440 120" className="wave">
              <path d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,64C960,75,1056,85,1152,80C1248,75,1344,53,1392,42.7L1440,32L1440,120L0,120Z"></path>
            </svg>
          </div>

        </div>
      </div>
    </div>
  )
}
